import React, { useEffect, useState, useCallback } from "react";
import { BellRing, Save, SendHorizonal, FlaskConical, Radio, ServerCog, ShieldCheck, ShieldAlert, RefreshCw, Lock, Users, UserCog, Wallet } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner } from "../../components/common";
import { RecipientScopeCard } from "../../components/RecipientScopeCard";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Skeleton } from "../../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { api, errMsg, fmtDate } from "../../lib/api";

const EVENTS = [
  { value: "demand_created", label: "New demand generated" },
  { value: "demand_corrected", label: "Demand corrected" },
  { value: "payment_received", label: "Payment received" },
  { value: "partial_payment", label: "Partial payment received" },
  { value: "payment_due_reminder", label: "Property Tax Due (payment due reminder)" },
  { value: "citizen_portal_payment", label: "Citizen Portal payment confirmation" },
  { value: "pending_payment_notice", label: "Pending payment notice (User Charge)" },  // Phase 220: DLT template, manual Send Notices
  { value: "final_notice", label: "Final Notice (dues)" },                             // Phase 221: manual Send Notices, awaiting DLT approval
  { value: "password_reset", label: "Forgot password / Password reset (staff)" },
];
const CITIZEN_PLACEHOLDERS = "Placeholders: {municipality} {owner_name} {pin} {amount} {receipt_no} {fy} {balance} — Citizen Portal: {address} {receipt_link} {payment_date} {payment_time}";
// Phase 219: SMS gateway choices (mobiCOMM = Dove Soft HTTP API, India DLT aware)
const SMS_PROVIDER_LABELS = { twilio: "Twilio", mobicomm: "mobiCOMM (Dove Soft)", custom: "Custom URL / API key" };
const MOBICOMM_DEFAULT_BASE_URL = "https://mobicomm.dove-sms.com";
const providerDisplay = (p) => (String(p || "").startsWith("mobicomm") ? "mobiCOMM" : String(p || "").startsWith("twilio") ? "Twilio" : p || "provider");
// Phase 125: staff account message — sent when someone uses "Forgot password?" on the sign-in page
const PLACEHOLDER_HELP_BY_EVENT = {
  // Phase 228: ONE message per property per action - several demands created together (same upload / approval) are combined
  demand_created: "Placeholders: {municipality} {owner_name} {pin} {amount} {fy} {pdf_link} {demand_lines} — when several demands are created together for one property (same upload file / same approval) the owner gets ONE message: {amount} is the combined total, {fy} the financial year(s), and {demand_lines} (WhatsApp / email only) lists each charge on its own line with the grand total, e.g. \"- Property Tax (2025-26): Rs.1,000.00 / - User Charges (2025-26): Rs.500.00 / Total: Rs.1,500.00\". The SMS is the approved DLT text and stays unchanged (only the amount is the combined total). The email also carries the property's demand notice PDF (all demands, grand total).",
  password_reset: "Placeholders: {municipality} {staff_name} {login_email} {reset_link} {expiry_minutes} {otp} — the reset link opens a page where the staff member sets a new password; it is valid for {expiry_minutes} minutes and works once. SMS is sent in the municipality's first configured language only (keep it short so the link stays intact); WhatsApp and email include every configured language.",
  // Phase 189: owner alert after a demand correction (Municipal Admin / Super Admin) — the reason is never part of the message
  demand_corrected: "Placeholders: {municipality} {owner_name} {pin} {tax_head} {fy} {old_amount} {new_amount} {paid} {outcome} {property_balance} {balance} {credit} — {outcome} is filled in the template's language: \"Balance due on this demand: Rs.X.\" or \"Rs.Y paid above the corrected demand is held as credit against your property.\" The admin's reason for the correction is never sent to the owner.",
  // Phase 220/221: DLT template - sent only from the Municipal Admin's Send Notices (MIS Reports → Dues notices)
  pending_payment_notice: "Placeholders (WhatsApp / email): {municipality} {balance} {pending_years} — total dues on the property and the number of financial years with a balance. The SMS is the approved DLT text. Sent only when a Municipal Admin picks properties in MIS Reports → Dues notices and confirms; never automatic.",
  // Phase 221: Final Notice - manual Send Notices only; blocked for live sending until a DLT-approved text + Template ID is saved here
  final_notice: "Placeholders: {municipality} {owner_name} {pin} {balance} {pending_years} — total dues across every year and the number of years pending. The English SMS is a draft: paste the DLT-approved text (with {#alp#}/{#num#} slots in the order Municipality, Owner, Amount, PIN, Years) and its Template ID here. Until then Municipal Admins can send it only in simulation mode.",
  payment_due_reminder: "Placeholders: {municipality} {owner_name} {pin} {balance} — shown to Municipal Admins as \"Property Tax Due\" in MIS Reports → Dues notices. Sent manually only (never on a schedule).",
};

export default function NotificationsConfig() {
  const [settings, setSettings] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [busy, setBusy] = useState(false);
  // templates
  const [tplEvent, setTplEvent] = useState("demand_created");
  const [tplLang, setTplLang] = useState("hi");
  const [tpl, setTpl] = useState(null);
  const [tplBusy, setTplBusy] = useState(false);
  // test
  const [testChannel, setTestChannel] = useState("sms");
  const [testTo, setTestTo] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [testBusy, setTestBusy] = useState(false);
  // Phase 220: pick any template + language and see the final message before sending
  const [testEvent, setTestEvent] = useState("payment_received");
  const [testLang, setTestLang] = useState("en");
  const [dltStatus, setDltStatus] = useState({});          // {event: {lang: {dlt_template_id, dlt_header}}}
  const [testPreview, setTestPreview] = useState(null);
  const [testPreviewError, setTestPreviewError] = useState("");
  const [testPreviewBusy, setTestPreviewBusy] = useState(false);
  // Phase 122: live gateway health (read-only probe — sends nothing)
  const [health, setHealth] = useState(null);
  const [healthBusy, setHealthBusy] = useState(false);
  // Phase 123: balance alert
  const [balanceBusy, setBalanceBusy] = useState(false);

  // Phase 126: a recipient-scope card (citizens / users & staff) changed the settings on the server
  const onScopeChanged = (data) => { setSettings(data); checkHealth(); };

  const checkBalanceNow = async () => {
    setBalanceBusy(true);
    try {
      const r = await api.post("/platform/notification-providers/balance-check");
      if (r.data.error) toast.error(r.data.error);
      else (r.data.low ? toast.warning : toast.success)(`Twilio balance: ${Number(r.data.balance).toFixed(2)} ${r.data.currency}`,
        { description: r.data.low ? `Below your ${Number(r.data.threshold).toFixed(2)} ${r.data.currency} threshold${r.data.alerted ? " — Super Admins alerted" : ""}` : "Above the alert threshold" });
      checkHealth();
    } catch (e) { toast.error(errMsg(e)); } finally { setBalanceBusy(false); }
  };

  const checkHealth = useCallback(async () => {
    setHealthBusy(true);
    try {
      const r = await api.get("/platform/notification-providers/health");
      setHealth(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setHealthBusy(false); }
  }, []);

  useEffect(() => {
    api.get("/platform/notification-settings").then((r) => setSettings(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/platform/notification-languages").then((r) => setLanguages(r.data)).catch(() => {});
    checkHealth();
  }, [checkHealth]);

  const loadTpl = useCallback(() => {
    setTpl(null);
    api.get("/platform/notification-templates", { params: { event: tplEvent, language: tplLang } })
      .then((r) => setTpl(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [tplEvent, tplLang]);
  useEffect(() => { loadTpl(); }, [loadTpl]);

  const set = (path, v) => setSettings((s) => {
    const next = JSON.parse(JSON.stringify(s));
    const keys = path.split(".");
    let o = next;
    for (let i = 0; i < keys.length - 1; i++) o = o[keys[i]] = o[keys[i]] || {};
    o[keys[keys.length - 1]] = v;
    return next;
  });

  const save = async () => {
    setBusy(true);
    try {
      // Phase 126: allow-lists are owned by the recipient-scope cards (audited endpoints) — never sent from here
      const { live_allow_list, live_allow_list_saved, staff_allow_list, staff_allow_list_saved, ...providerSettings } = settings;
      const r = await api.put("/platform/notification-settings", providerSettings);
      setSettings(r.data);
      toast.success("Notification settings saved");
      checkHealth();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const saveTpl = async () => {
    setTplBusy(true);
    try {
      await api.put("/platform/notification-templates", { event: tplEvent, language: tplLang, ...tpl });
      toast.success("Template saved");
      loadTpl();          // Phase 220: refresh DLT approval / lock state
      loadDltStatus();
    } catch (e) { toast.error(errMsg(e)); } finally { setTplBusy(false); }
  };

  // Phase 220: which language versions are DLT approved (only those can be chosen for an SMS test)
  const loadDltStatus = useCallback(() => {
    api.get("/platform/notification-dlt-status").then((r) => setDltStatus(r.data.approved || {})).catch(() => {});
  }, []);
  useEffect(() => { loadDltStatus(); }, [loadDltStatus]);

  const approvedLangsFor = (ev) => Object.keys(dltStatus[ev] || {});
  const smsLangAllowed = (ev, code) => { const a = approvedLangsFor(ev); return a.length === 0 || a.includes(code); };
  // when the chosen language is not allowed for SMS, jump to the first approved one
  useEffect(() => {
    if (testChannel !== "sms") return;
    const a = approvedLangsFor(testEvent);
    if (a.length && !a.includes(testLang)) setTestLang(a.includes("en") ? "en" : a[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testChannel, testEvent, dltStatus]);

  useEffect(() => {
    let cancelled = false;
    setTestPreview(null); setTestPreviewError(""); setTestPreviewBusy(true);
    api.get("/platform/notification-test/preview", { params: { channel: testChannel, event: testEvent, language: testLang } })
      .then((r) => { if (!cancelled) setTestPreview(r.data); })
      .catch((e) => { if (!cancelled) setTestPreviewError(errMsg(e)); })
      .finally(() => { if (!cancelled) setTestPreviewBusy(false); });
    return () => { cancelled = true; };
  }, [testChannel, testEvent, testLang, dltStatus]);

  const runTest = async () => {
    if (!testTo.trim()) return toast.error("Enter a recipient (phone/email)");
    if (testPreviewError) return toast.error("Fix the template selection first", { description: testPreviewError });
    setTestBusy(true);
    setTestResult(null);
    try {
      const r = await api.post("/platform/notification-test", { channel: testChannel, to: testTo.trim(), event: testEvent, language: testLang });
      setTestResult(r.data);
      // Phase 219: say plainly whether the gateway accepted the message (and why not, when it did not)
      const d = r.data;
      if (d.status === "sent") toast.success(`Test message sent via ${providerDisplay(d.provider)}`, { description: d.meta?.provider_message_id ? `Message ID ${d.meta.provider_message_id}` : undefined });
      else if (d.status === "failed") toast.error("Test message was not accepted", { description: d.error || d.summary });
      else if (d.status === "suppressed") toast.warning("Test message suppressed", { description: d.summary || d.error });
      else toast.info("Simulated — nothing was sent", { description: "Switch simulation mode off to send a real test." });
    } catch (e) { toast.error(errMsg(e)); } finally { setTestBusy(false); }
  };

  if (!settings) return <Spinner />;
  const sim = !!settings.simulation_mode;
  const envTwilio = settings.twilio?.source === "env";
  const tw = health?.twilio;
  const mc = settings.mobicomm || {};          // Phase 219
  const mcHealth = health?.mobicomm;
  const citizenCount = (settings.live_allow_list || []).length;
  const staffCount = (settings.staff_allow_list || []).length;
  const scopeText = (label, n, unit) => `${label}: ${n > 0 ? `test list (${n} ${unit}${n === 1 ? "" : "s"})` : "open to ALL"}`;
  const gw = health?.gateway_status;
  const lowBalance = !!gw?.low && !sim;

  return (
    <div className="space-y-4" data-testid="notifications-config-page">
      <PageHeader icon={BellRing} title="Notifications Engine"
        subtitle="Global WhatsApp / SMS / Email providers for all municipalities" />

      <Card className={sim ? "border-amber-300 bg-amber-50/60" : "border-emerald-300 bg-emerald-50/50"}>
        <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <FlaskConical className={`h-5 w-5 ${sim ? "text-amber-600" : "text-emerald-600"}`} />
            <div>
              <p className="text-sm font-semibold">{sim ? "Simulation mode is ON" : "Live sending is ON"}</p>
              <p className="text-xs text-muted-foreground" data-testid="live-scope-summary">
                {sim ? "Messages are composed and logged in each municipality's outbox — nothing is actually sent."
                  : `${scopeText("Citizens", citizenCount, "number")} · ${scopeText("Users & Staff", staffCount, "recipient")}. Recipients outside a test list are logged as “suppressed”; when a list is open, gateway charges apply.`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs">Simulation</Label>
            <Switch checked={sim} onCheckedChange={(v) => set("simulation_mode", v)} data-testid="simulation-mode-switch" />
          </div>
        </CardContent>
      </Card>

      {lowBalance && (
        <Card className="border-destructive/40 bg-destructive/5" data-testid="low-balance-banner">
          <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <Wallet className="h-5 w-5 text-destructive" />
              <div>
                <p className="text-sm font-semibold">Twilio balance is low: {Number(gw.balance).toFixed(2)} {gw.currency}</p>
                <p className="text-xs text-muted-foreground">Below your {Number(gw.threshold ?? settings.balance_alert_threshold).toFixed(2)} {gw.currency || "USD"} alert threshold — top up in the Twilio console so citizen SMS keep flowing. Checked {gw.checked_at ? fmtDate(gw.checked_at) : "just now"}.</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={checkBalanceNow} disabled={balanceBusy} data-testid="low-balance-recheck">
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${balanceBusy ? "animate-spin" : ""}`} /> Re-check
            </Button>
          </CardContent>
        </Card>
      )}

      {!sim && (
        <section className="space-y-3" data-testid="recipient-scope-section">
          <div className="flex items-start justify-between gap-3 flex-wrap px-1">
            <div>
              <p className="text-sm font-semibold flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> Who receives real messages</p>
              <p className="text-xs text-muted-foreground mt-1">Two independent switches — one for citizens, one for users & staff. Each has its own test allow-list and its own “Go public” confirmation. Changes apply immediately and are audited.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <RecipientScopeCard audience="citizen" icon={Users} settings={settings} onChanged={onScopeChanged} />
            <RecipientScopeCard audience="staff" icon={UserCog} settings={settings} onChanged={onScopeChanged} />
          </div>
        </section>
      )}

      <Tabs defaultValue="providers">
        <TabsList>
          <TabsTrigger value="providers" data-testid="tab-providers">Providers</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="test" data-testid="tab-test">Test Send</TabsTrigger>
        </TabsList>

        <TabsContent value="providers" className="space-y-4 mt-3">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="shadow-sm">
              <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
                <CardTitle className="text-base font-heading flex items-center gap-2"><Radio className="h-4 w-4" /> SMS</CardTitle>
                <Switch checked={settings.channels.sms} onCheckedChange={(v) => set("channels.sms", v)} data-testid="sms-enabled-switch" />
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1">
                  <Label>Provider</Label>
                  <Select value={settings.providers.sms} onValueChange={(v) => v && set("providers.sms", v)}>
                    <SelectTrigger data-testid="sms-provider-select"><SelectValue>{SMS_PROVIDER_LABELS[settings.providers.sms] || settings.providers.sms}</SelectValue></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="twilio" data-testid="sms-provider-option-twilio">Twilio</SelectItem>
                      <SelectItem value="mobicomm" data-testid="sms-provider-option-mobicomm">mobiCOMM (Dove Soft)</SelectItem>
                      <SelectItem value="custom" data-testid="sms-provider-option-custom">Custom URL / API key</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {settings.providers.sms === "mobicomm" && (
                  /* Phase 219: mobiCOMM (Dove Soft) HTTP SMS API */
                  <div className="space-y-3" data-testid="mobicomm-settings">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div className="space-y-1"><Label className="text-xs">Username</Label>
                        <Input value={mc.username || ""} onChange={(e) => set("mobicomm.username", e.target.value)} placeholder="mobiCOMM login user" autoComplete="off" data-testid="mobicomm-username-input" /></div>
                      <div className="space-y-1"><Label className="text-xs">API key</Label>
                        <Input type="password" value={mc.api_key || ""} onChange={(e) => set("mobicomm.api_key", e.target.value)} placeholder="API key from the mobiCOMM panel" autoComplete="new-password" data-testid="mobicomm-apikey-input" /></div>
                      <div className="space-y-1"><Label className="text-xs">Sender ID (DLT approved header)</Label>
                        <Input value={mc.sender_id || ""} onChange={(e) => set("mobicomm.sender_id", e.target.value.toUpperCase())} placeholder="e.g. MUNCON" maxLength={11} data-testid="mobicomm-senderid-input" /></div>
                      <div className="space-y-1"><Label className="text-xs">Base URL</Label>
                        <Input value={mc.base_url ?? MOBICOMM_DEFAULT_BASE_URL} onChange={(e) => set("mobicomm.base_url", e.target.value)} placeholder={MOBICOMM_DEFAULT_BASE_URL} data-testid="mobicomm-baseurl-input" /></div>
                      <div className="space-y-1 sm:col-span-2"><Label className="text-xs">DLT Entity ID <span className="text-muted-foreground font-normal">(optional — Principal Entity ID from the DLT portal)</span></Label>
                        <Input value={mc.dlt_entity_id || ""} onChange={(e) => set("mobicomm.dlt_entity_id", e.target.value)} placeholder="e.g. 1101XXXXXXXXXXXXXXX" data-testid="mobicomm-entityid-input" /></div>
                    </div>
                    <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs space-y-1.5" data-testid="mobicomm-readiness-panel">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-medium flex items-center gap-1.5"><ServerCog className="h-3.5 w-3.5" /> Gateway settings</p>
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={checkHealth} disabled={healthBusy} data-testid="mobicomm-health-refresh">
                          <RefreshCw className={`h-3 w-3 mr-1 ${healthBusy ? "animate-spin" : ""}`} /> Check
                        </Button>
                      </div>
                      {!mcHealth ? (
                        <p className="text-muted-foreground">{healthBusy ? "Checking…" : "Not checked yet"}</p>
                      ) : mcHealth.configured ? (
                        <p className="flex items-center gap-1 text-[hsl(var(--success))]" data-testid="mobicomm-health-ok">
                          <ShieldCheck className="h-3.5 w-3.5" /> Saved settings are complete · user {mcHealth.username} · sender {mcHealth.sender_id}{mcHealth.dlt_entity_id ? ` · entity ${mcHealth.dlt_entity_id}` : ""}. Use the Test Send tab to confirm the account accepts messages.
                        </p>
                      ) : (
                        <p className="flex items-center gap-1 text-destructive" data-testid="mobicomm-health-missing">
                          <ShieldAlert className="h-3.5 w-3.5" /> Saved settings incomplete — missing {mcHealth.missing?.join(", ") || "fields"}. Messages stay “simulated” until all three are saved.
                        </p>
                      )}
                      <p className="text-muted-foreground">
                        Engine calls <span className="font-mono">{(mc.base_url || MOBICOMM_DEFAULT_BASE_URL).replace(/\/$/, "")}/submitsms.jsp</span> with user, key, mobile, message, senderid, accusage=1{mc.dlt_entity_id ? ", entityid" : ""} (+ tempid when a template has a DLT Template ID; unicode=1 for Hindi / non-Latin text). Indian 10-digit numbers are sent as 91XXXXXXXXXX.
                      </p>
                    </div>
                  </div>
                )}
                {settings.providers.sms === "twilio" && (
                  <div className="space-y-3">
                    {envTwilio && (
                      <div className="flex items-start gap-2 rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-xs" data-testid="twilio-env-banner">
                        <Lock className="h-3.5 w-3.5 mt-0.5 text-primary shrink-0" />
                        <p>Twilio credentials are supplied by the server environment (<code>TWILIO_ACCOUNT_SID</code>, <code>TWILIO_AUTH_TOKEN</code>, <code>TWILIO_FROM_NUMBER</code>) and take precedence — they are read-only here.</p>
                      </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div className="space-y-1"><Label className="text-xs">Account SID</Label>
                        <Input value={settings.twilio.account_sid} readOnly={envTwilio} disabled={envTwilio} onChange={(e) => set("twilio.account_sid", e.target.value)} data-testid="twilio-sid-input" /></div>
                      <div className="space-y-1"><Label className="text-xs">Auth Token</Label>
                        <Input type="password" value={settings.twilio.auth_token} readOnly={envTwilio} disabled={envTwilio} onChange={(e) => set("twilio.auth_token", e.target.value)} data-testid="twilio-token-input" /></div>
                      <div className="space-y-1 sm:col-span-2"><Label className="text-xs">SMS From number (e.g. +1415XXXXXXX)</Label>
                        <Input value={settings.twilio.sms_from} readOnly={envTwilio} disabled={envTwilio} onChange={(e) => set("twilio.sms_from", e.target.value)} data-testid="twilio-smsfrom-input" /></div>
                    </div>
                    {/* connection check — read-only probe, sends nothing */}
                    <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs space-y-1.5" data-testid="twilio-health-panel">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-medium flex items-center gap-1.5"><ServerCog className="h-3.5 w-3.5" /> Gateway connection</p>
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={checkHealth} disabled={healthBusy} data-testid="twilio-health-refresh">
                          <RefreshCw className={`h-3 w-3 mr-1 ${healthBusy ? "animate-spin" : ""}`} /> Check
                        </Button>
                      </div>
                      {!tw ? (
                        <p className="text-muted-foreground">{healthBusy ? "Checking…" : "Not checked yet"}</p>
                      ) : tw.ok ? (
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1" data-testid="twilio-health-ok">
                          <span className="flex items-center gap-1 text-[hsl(var(--success))] col-span-2"><ShieldCheck className="h-3.5 w-3.5" /> Connected · {tw.account_name} · SID …{tw.account_sid_hint}</span>
                          <span className="text-muted-foreground">Account</span><span className="capitalize">{tw.account_status} · {tw.account_type}{tw.account_type === "Trial" ? " (only verified numbers can receive SMS)" : ""}</span>
                          <span className="text-muted-foreground">From number</span><span>{tw.from_number} · {tw.from_number_owned ? (tw.from_sms_capable ? "owned, SMS-capable" : "owned, NOT SMS-capable") : tw.from_number_owned === false ? "not found on this account" : "ownership unknown"}</span>
                          <span className="text-muted-foreground">Balance</span><span>{tw.balance || "n/a"}</span>
                          <span className="text-muted-foreground">Delivery receipts</span><span>{health.status_callback_configured ? "on (status webhook)" : "off — set PUBLIC_BASE_URL"}</span>
                        </div>
                      ) : (
                        <p className="flex items-center gap-1 text-destructive" data-testid="twilio-health-error"><ShieldAlert className="h-3.5 w-3.5" /> {tw.error || "Twilio check failed"}</p>
                      )}
                    </div>
                    {/* Phase 123: low-balance alert */}
                    <div className="rounded-md border px-3 py-2 text-xs space-y-2" data-testid="balance-alert-panel">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-medium flex items-center gap-1.5"><Wallet className="h-3.5 w-3.5" /> Low-balance alert</p>
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={checkBalanceNow} disabled={balanceBusy} data-testid="balance-check-now">
                          <RefreshCw className={`h-3 w-3 mr-1 ${balanceBusy ? "animate-spin" : ""}`} /> Check now
                        </Button>
                      </div>
                      <div className="flex items-end gap-3 flex-wrap">
                        <div className="space-y-1">
                          <Label className="text-xs">Alert when balance falls below (USD)</Label>
                          <Input type="number" min="0" step="0.5" className="h-9 w-40" value={settings.balance_alert_threshold ?? 5}
                            onChange={(e) => set("balance_alert_threshold", e.target.value)} data-testid="balance-threshold-input" />
                        </div>
                        <p className="text-muted-foreground pb-2">
                          {Number(settings.balance_alert_threshold) > 0
                            ? `Super Admins get an in-app alert (bell) when the balance drops below the threshold — checked every 30 min and after real sends, at most once a day while low.`
                            : "Off — set a value to enable the alert."}
                          {gw?.checked_at && <> Last check: {Number(gw.balance).toFixed(2)} {gw.currency} · {fmtDate(gw.checked_at)}{gw.low ? " · LOW" : ""}.</>}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {settings.providers.sms === "custom" && (
                  <div className="grid grid-cols-1 gap-2">
                    <div className="space-y-1"><Label className="text-xs">POST URL</Label>
                      <Input value={settings.custom_sms.url} onChange={(e) => set("custom_sms.url", e.target.value)} placeholder="https://provider.example.com/send" data-testid="custom-sms-url-input" /></div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1"><Label className="text-xs">API key header</Label>
                        <Input value={settings.custom_sms.api_key_header} onChange={(e) => set("custom_sms.api_key_header", e.target.value)} /></div>
                      <div className="space-y-1"><Label className="text-xs">API key</Label>
                        <Input type="password" value={settings.custom_sms.api_key} onChange={(e) => set("custom_sms.api_key", e.target.value)} /></div>
                    </div>
                    <p className="text-[11px] text-muted-foreground">Engine POSTs JSON: {"{ to, message, channel }"}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="shadow-sm">
              <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
                <CardTitle className="text-base font-heading">WhatsApp</CardTitle>
                <Switch checked={settings.channels.whatsapp} onCheckedChange={(v) => set("channels.whatsapp", v)} data-testid="whatsapp-enabled-switch" />
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1">
                  <Label>Provider</Label>
                  <Select value={settings.providers.whatsapp} onValueChange={(v) => v && set("providers.whatsapp", v)}>
                    <SelectTrigger data-testid="whatsapp-provider-select"><SelectValue>{settings.providers.whatsapp === "twilio" ? "Twilio" : "Custom URL / API key"}</SelectValue></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="twilio">Twilio</SelectItem>
                      <SelectItem value="custom">Custom URL / API key</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {settings.providers.whatsapp === "twilio" ? (
                  <div className="space-y-1">
                    <Label className="text-xs">WhatsApp From number (uses the same Twilio SID/Token as SMS)</Label>
                    <Input value={settings.twilio.whatsapp_from} onChange={(e) => set("twilio.whatsapp_from", e.target.value)}
                      placeholder="+1415XXXXXXX" data-testid="twilio-wafrom-input" />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-2">
                    <div className="space-y-1"><Label className="text-xs">POST URL</Label>
                      <Input value={settings.custom_whatsapp.url} onChange={(e) => set("custom_whatsapp.url", e.target.value)} placeholder="https://provider.example.com/send" /></div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1"><Label className="text-xs">API key header</Label>
                        <Input value={settings.custom_whatsapp.api_key_header} onChange={(e) => set("custom_whatsapp.api_key_header", e.target.value)} /></div>
                      <div className="space-y-1"><Label className="text-xs">API key</Label>
                        <Input type="password" value={settings.custom_whatsapp.api_key} onChange={(e) => set("custom_whatsapp.api_key", e.target.value)} /></div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="shadow-sm lg:col-span-2">
              <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
                <CardTitle className="text-base font-heading">Email</CardTitle>
                <Switch checked={settings.channels.email} onCheckedChange={(v) => set("channels.email", v)} data-testid="email-enabled-switch" />
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-1 max-w-xs">
                  <Label>Provider</Label>
                  <Select value={settings.providers.email} onValueChange={(v) => v && set("providers.email", v)}>
                    <SelectTrigger data-testid="email-provider-select">
                      <SelectValue>{{ sendgrid: "SendGrid", smtp: "Gmail / Custom SMTP", custom: "Custom URL / API key" }[settings.providers.email]}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sendgrid">SendGrid</SelectItem>
                      <SelectItem value="smtp">Gmail / Custom SMTP</SelectItem>
                      <SelectItem value="custom">Custom URL / API key</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {settings.providers.email === "sendgrid" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="space-y-1"><Label className="text-xs">SendGrid API key</Label>
                      <Input type="password" value={settings.sendgrid.api_key} onChange={(e) => set("sendgrid.api_key", e.target.value)} data-testid="sendgrid-key-input" /></div>
                    <div className="space-y-1"><Label className="text-xs">Verified From email</Label>
                      <Input value={settings.sendgrid.from_email} onChange={(e) => set("sendgrid.from_email", e.target.value)} placeholder="noreply@municipality.gov.in" data-testid="sendgrid-from-input" /></div>
                  </div>
                )}
                {settings.providers.email === "smtp" && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <div className="space-y-1"><Label className="text-xs">SMTP host</Label>
                      <Input value={settings.smtp.host} onChange={(e) => set("smtp.host", e.target.value)} placeholder="smtp.gmail.com" /></div>
                    <div className="space-y-1"><Label className="text-xs">Port</Label>
                      <Input type="number" value={settings.smtp.port} onChange={(e) => set("smtp.port", Number(e.target.value) || 587)} /></div>
                    <div className="space-y-1"><Label className="text-xs">From email</Label>
                      <Input value={settings.smtp.from_email} onChange={(e) => set("smtp.from_email", e.target.value)} /></div>
                    <div className="space-y-1"><Label className="text-xs">Username</Label>
                      <Input value={settings.smtp.username} onChange={(e) => set("smtp.username", e.target.value)} /></div>
                    <div className="space-y-1"><Label className="text-xs">Password / App password</Label>
                      <Input type="password" value={settings.smtp.password} onChange={(e) => set("smtp.password", e.target.value)} /></div>
                    <div className="flex items-end gap-2 pb-1">
                      <Switch checked={settings.smtp.use_tls} onCheckedChange={(v) => set("smtp.use_tls", v)} />
                      <Label className="text-xs">Use TLS</Label>
                    </div>
                  </div>
                )}
                {settings.providers.email === "custom" && (
                  <div className="grid grid-cols-1 gap-2">
                    <div className="space-y-1"><Label className="text-xs">POST URL</Label>
                      <Input value={settings.custom_email.url} onChange={(e) => set("custom_email.url", e.target.value)} placeholder="https://provider.example.com/send-email" /></div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1"><Label className="text-xs">API key header</Label>
                        <Input value={settings.custom_email.api_key_header} onChange={(e) => set("custom_email.api_key_header", e.target.value)} /></div>
                      <div className="space-y-1"><Label className="text-xs">API key</Label>
                        <Input type="password" value={settings.custom_email.api_key} onChange={(e) => set("custom_email.api_key", e.target.value)} /></div>
                    </div>
                    <p className="text-[11px] text-muted-foreground">Engine POSTs JSON: {"{ to, subject, message, channel }"}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          <Card className="shadow-sm">
            <CardContent className="p-4 space-y-1 max-w-2xl">
              <Label>Public base URL (used to build demand-notice PDF links in messages)</Label>
              <Input value={settings.public_base_url || ""} onChange={(e) => set("public_base_url", e.target.value)}
                placeholder="https://your-app-domain.com (leave blank to use the server default)" data-testid="public-base-url-input" />
              <p className="text-[11px] text-muted-foreground">
                Demand alerts include a link like <span className="font-mono">{"{base}"}/api/public/{"{ulb}"}/demand-notice/{"{token}"}</span>. Set this to your live domain after deployment.
              </p>
            </CardContent>
          </Card>
          <Button onClick={save} disabled={busy} data-testid="save-notification-settings-button">
            <Save className="h-4 w-4 mr-1.5" /> {busy ? "Saving..." : "Save provider settings"}
          </Button>
        </TabsContent>

        <TabsContent value="templates" className="space-y-4 mt-3">
          <Card className="shadow-sm">
            <CardHeader className="pb-2"><CardTitle className="text-base font-heading">Message templates</CardTitle>
              <p className="text-xs text-muted-foreground" data-testid="template-placeholder-help">{PLACEHOLDER_HELP_BY_EVENT[tplEvent] || CITIZEN_PLACEHOLDERS}</p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
                <div className="space-y-1">
                  <Label>Event</Label>
                  <Select value={tplEvent} onValueChange={(v) => v && setTplEvent(v)}>
                    <SelectTrigger data-testid="template-event-select"><SelectValue>{EVENTS.find((e) => e.value === tplEvent)?.label}</SelectValue></SelectTrigger>
                    <SelectContent>
                      {EVENTS.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Language (English + 22 scheduled languages)</Label>
                  <Select value={tplLang} onValueChange={(v) => v && setTplLang(v)}>
                    <SelectTrigger data-testid="template-language-select"><SelectValue>{languages.find((l) => l.code === tplLang)?.name || tplLang}</SelectValue></SelectTrigger>
                    <SelectContent>
                      {languages.map((l) => <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {!tpl ? <Spinner /> : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <Label className="text-xs">SMS body</Label>
                      {/* Phase 220: DLT status of this language version */}
                      {tpl.dlt_approved ? (
                        <Badge variant="secondary" className="text-[10px] gap-1" data-testid="template-dlt-approved-badge"><ShieldCheck className="h-3 w-3" /> DLT approved — used for SMS</Badge>
                      ) : tpl.sms_locked ? (
                        <Badge variant="outline" className="text-[10px] gap-1 border-amber-500/50 text-amber-700 dark:text-amber-400" data-testid="template-dlt-locked-badge"><Lock className="h-3 w-3" /> Not DLT approved — not sent as SMS</Badge>
                      ) : null}
                    </div>
                    <Textarea rows={tpl.is_dlt_text ? 6 : 4} value={tpl.sms || ""} onChange={(e) => setTpl({ ...tpl, sms: e.target.value })} disabled={!!tpl.sms_locked && !(tpl.dlt_template_id || "").trim()} className={tpl.is_dlt_text ? "font-mono text-xs" : ""} data-testid="template-sms-textarea" />
                    {tpl.sms_locked && !(tpl.dlt_template_id || "").trim() && (
                      <p className="text-[11px] text-amber-700 dark:text-amber-400" data-testid="template-sms-locked-note">
                        Only the DLT-approved version{tpl.event_dlt_languages?.length > 1 ? "s" : ""} ({(tpl.event_dlt_languages || []).map((c) => languages.find((l) => l.code === c)?.name || c).join(", ")}) go out as SMS. Enter this language's DLT Template ID below to unlock its SMS text once the operator has approved it.
                      </p>
                    )}
                    {tpl.is_dlt_text && !tpl.sms_locked && (
                      <div className="rounded-md border bg-muted/30 px-2.5 py-2 text-[11px] space-y-1" data-testid="template-dlt-variable-guide">
                        <p className="font-medium">Approved DLT text — keep every space, full stop and symbol exactly as approved. The slots are filled in this order:</p>
                        <ol className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5 list-decimal list-inside text-muted-foreground">
                          {(tpl.dlt_variables || []).map((v) => (
                            <li key={v.position} data-testid={`template-dlt-variable-${v.position}`}><span className="font-mono">{`{#${v.type}#}`}</span> → {v.label}</li>
                          ))}
                        </ol>
                        <p className="text-muted-foreground">Amounts are written as 1500.00 (no commas); text values are trimmed to 30 characters as DLT requires.</p>
                      </div>
                    )}
                  </div>
                  <div className="space-y-1"><Label className="text-xs">WhatsApp body</Label>
                    <Textarea rows={4} value={tpl.whatsapp || ""} onChange={(e) => setTpl({ ...tpl, whatsapp: e.target.value })} data-testid="template-whatsapp-textarea" /></div>
                  <div className="space-y-1"><Label className="text-xs">Email subject</Label>
                    <Input value={tpl.email_subject || ""} onChange={(e) => setTpl({ ...tpl, email_subject: e.target.value })} data-testid="template-email-subject-input" /></div>
                  <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
                    <div className="space-y-1"><Label className="text-xs">DLT Template ID <span className="text-muted-foreground font-normal">(India DLT — approval for SMS)</span></Label>
                      <Input value={tpl.dlt_template_id || ""} onChange={(e) => setTpl({ ...tpl, dlt_template_id: e.target.value.replace(/\s+/g, "") })} placeholder="e.g. 1107XXXXXXXXXXXXXXX" data-testid="template-dlt-id-input" /></div>
                    <div className="space-y-1 sm:w-36"><Label className="text-xs">Header <span className="text-muted-foreground font-normal">(sender name)</span></Label>
                      <Input value={tpl.dlt_header || ""} onChange={(e) => setTpl({ ...tpl, dlt_header: e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 11) })} placeholder="e.g. GESIL" data-testid="template-dlt-header-input" /></div>
                    <p className="text-[11px] text-muted-foreground sm:col-span-2">The Template ID is sent as <span className="font-mono">tempid</span> with every SMS built from this version. When the Header is filled it is used as the SMS sender ID instead of the provider's Sender ID (mobiCOMM).</p>
                  </div>
                  <div className="space-y-1 lg:col-span-2"><Label className="text-xs">Email body</Label>
                    <Textarea rows={6} value={tpl.email_body || ""} onChange={(e) => setTpl({ ...tpl, email_body: e.target.value })} data-testid="template-email-body-textarea" /></div>
                </div>
              )}
              <Button onClick={saveTpl} disabled={tplBusy || !tpl} data-testid="save-template-button">
                <Save className="h-4 w-4 mr-1.5" /> {tplBusy ? "Saving..." : "Save template"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="test" className="mt-3">
          <Card className="shadow-sm max-w-3xl">
            <CardHeader className="pb-2"><CardTitle className="text-base font-heading">Send a test message</CardTitle>
              <p className="text-xs text-muted-foreground" data-testid="test-send-help">Pick any template; the final message is shown below with sample data before you send it. SMS goes via <span className="font-medium">{SMS_PROVIDER_LABELS[settings.providers.sms] || settings.providers.sms}</span>{sim ? " — simulation mode is ON, so nothing is actually sent." : "."}</p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Template</Label>
                  <Select value={testEvent} onValueChange={(v) => v && setTestEvent(v)}>
                    <SelectTrigger data-testid="test-template-select"><SelectValue>{EVENTS.find((e) => e.value === testEvent)?.label}</SelectValue></SelectTrigger>
                    <SelectContent>
                      {EVENTS.map((e) => (
                        <SelectItem key={e.value} value={e.value} data-testid={`test-template-option-${e.value}`}>
                          {e.label}{approvedLangsFor(e.value).length ? " · DLT" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Language</Label>
                  <Select value={testLang} onValueChange={(v) => v && setTestLang(v)}>
                    <SelectTrigger data-testid="test-language-select"><SelectValue>{languages.find((l) => l.code === testLang)?.name || testLang}</SelectValue></SelectTrigger>
                    <SelectContent>
                      {languages.map((l) => {
                        const allowed = testChannel !== "sms" || smsLangAllowed(testEvent, l.code);
                        return (
                          <SelectItem key={l.code} value={l.code} disabled={!allowed} data-testid={`test-language-option-${l.code}`}>
                            {l.name}{!allowed ? " — not DLT approved" : ""}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  {testChannel === "sms" && approvedLangsFor(testEvent).length > 0 && (
                    <p className="text-[11px] text-muted-foreground" data-testid="test-language-dlt-note">Only DLT-approved language versions can be sent as SMS for this template.</p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label>Channel</Label>
                  <Select value={testChannel} onValueChange={(v) => v && setTestChannel(v)}>
                    <SelectTrigger data-testid="test-channel-select"><SelectValue>{{ sms: "SMS", whatsapp: "WhatsApp", email: "Email" }[testChannel]}</SelectValue></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sms">SMS</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>{testChannel === "email" ? "Recipient email" : "Recipient mobile"}</Label>
                  <Input value={testTo} onChange={(e) => setTestTo(e.target.value)}
                    placeholder={testChannel === "email" ? "someone@example.com" : "9876543210"} data-testid="test-recipient-input" />
                </div>
              </div>

              {/* Phase 220: the exact message that will go out */}
              <div className="rounded-lg border bg-muted/30 p-3 space-y-2" data-testid="test-preview-panel">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-xs font-medium flex items-center gap-1.5"><FlaskConical className="h-3.5 w-3.5" /> Final message (sample data)</p>
                  {testPreview?.dlt_template_id && (
                    <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
                      <Badge variant="secondary" className="text-[10px] gap-1" data-testid="test-preview-dlt-id"><ShieldCheck className="h-3 w-3" /> DLT {testPreview.dlt_template_id}</Badge>
                      {testPreview.sender_id_used && <Badge variant="outline" className="text-[10px]" data-testid="test-preview-header">Sender {testPreview.sender_id_used}</Badge>}
                    </div>
                  )}
                </div>
                {testPreviewBusy ? (
                  <Skeleton className="h-16 w-full" />
                ) : testPreviewError ? (
                  <p className="text-xs text-destructive flex items-start gap-1" data-testid="test-preview-error"><ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {testPreviewError}</p>
                ) : testPreview ? (
                  <>
                    {testPreview.subject && <p className="text-xs"><span className="text-muted-foreground">Subject:</span> {testPreview.subject}</p>}
                    <pre className="text-xs whitespace-pre-wrap font-sans border rounded p-2 bg-background" data-testid="test-preview-message">{testPreview.message || "(this template has no text for the selected channel)"}</pre>
                    {testPreview.length && (
                      <p className="text-[11px] text-muted-foreground" data-testid="test-preview-length">
                        {testPreview.length.chars} characters · {testPreview.length.segments} SMS segment{testPreview.length.segments === 1 ? "" : "s"}{testPreview.length.unicode ? " · Unicode" : ""}
                      </p>
                    )}
                    {testPreview.variables?.length > 0 && (
                      <details className="text-[11px]" data-testid="test-preview-variables">
                        <summary className="cursor-pointer text-muted-foreground">How the DLT slots were filled</summary>
                        <ol className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5 list-decimal list-inside">
                          {testPreview.variables.map((v) => (
                            <li key={v.position} className={v.ok ? "" : "text-destructive"}>
                              <span className="font-mono">{`{#${v.type}#}`}</span> {v.label}: <span className="font-medium">{v.value}</span>
                            </li>
                          ))}
                        </ol>
                      </details>
                    )}
                  </>
                ) : null}
              </div>

              <Button onClick={runTest} disabled={testBusy || testPreviewBusy || !!testPreviewError} data-testid="send-test-button">
                <SendHorizonal className="h-4 w-4 mr-1.5" /> {testBusy ? "Sending..." : "Send test"}
              </Button>
              {testResult && (
                <div className={`border rounded-lg p-3 text-sm space-y-1.5 ${testResult.status === "failed" ? "border-destructive/40 bg-destructive/5" : testResult.status === "sent" ? "border-[hsl(var(--success))]/40 bg-muted/40" : "bg-muted/40"}`} data-testid="test-result-panel">
                  <p className="flex items-center gap-2 flex-wrap">Status:
                    <Badge variant={testResult.status === "failed" ? "destructive" : "secondary"} data-testid="test-result-status">{testResult.status}</Badge>
                    <span className="text-xs text-muted-foreground">via {testResult.provider}</span>
                    {testResult.event && <span className="text-xs text-muted-foreground" data-testid="test-result-template">· {EVENTS.find((e) => e.value === testResult.event)?.label || testResult.event} ({languages.find((l) => l.code === testResult.language)?.name || testResult.language})</span>}
                  </p>
                  {testResult.status === "sent" && (
                    <p className="text-xs flex items-center gap-1 text-[hsl(var(--success))]" data-testid="test-result-success">
                      <ShieldCheck className="h-3.5 w-3.5" /> {testResult.summary || `Accepted by ${providerDisplay(testResult.provider)}`}
                    </p>
                  )}
                  {testResult.error && (
                    <p className="text-xs text-destructive flex items-start gap-1" data-testid="test-result-error">
                      <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" /> <span>{testResult.error}</span>
                    </p>
                  )}
                  {testResult.meta?.provider_message_id && (
                    <p className="text-xs text-muted-foreground" data-testid="test-result-sid">
                      {providerDisplay(testResult.provider)} message {String(testResult.provider).startsWith("twilio") ? "SID" : "ID"} <span className="font-mono">{testResult.meta.provider_message_id}</span>{testResult.meta.provider_status ? ` · initial status: ${testResult.meta.provider_status}` : ""}
                    </p>
                  )}
                  {String(testResult.provider).startsWith("mobicomm") && testResult.meta && (
                    <p className="text-[11px] text-muted-foreground" data-testid="test-result-mobicomm-meta">
                      Sent to <span className="font-mono">{testResult.meta.gateway_mobile || testResult.to}</span>
                      {testResult.meta.dlt_template_id ? <> · DLT template <span className="font-mono">{testResult.meta.dlt_template_id}</span></> : " · no DLT Template ID on this template"}
                      {testResult.meta.unicode ? " · unicode" : ""}
                      {testResult.meta.mobicomm_response ? <> · gateway reply <span className="font-mono">{testResult.meta.mobicomm_response}</span></> : null}
                    </p>
                  )}
                  {testResult.status === "suppressed" && (
                    <p className="text-xs text-muted-foreground">Add this number to the live allow-list above (and save) to let the test through.</p>
                  )}
                  <p className="text-xs whitespace-pre-wrap border rounded p-2 bg-background">{testResult.preview}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
